export const getTypography = (palette) => ({
      // Global: Noto Sans is the ONLY permitted typeface
      fontFamily: '"Noto Sans", sans-serif',
      // Constant base: the a11y font-size control scales <html> (App.js), and the
      // rem-based variants below follow it; feeding it here too would double-scale.
      fontSize: 14,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeight: 500,
      fontWeightMedium: 600,
      fontWeightBold: 700,

      // Custom XL banner — project extension, not in Figma style guide
      h0: {
        fontSize: "3rem",
        fontWeight: 800,
        lineHeight: 1.15,
        fontFamily: '"Noto Sans", sans-serif',
      },
      // Hero / H1 — Figma node 8:3419 (2rem/32px, weight 800, line-height 1.15)
      h1: {
        fontSize: "2rem",
        fontWeight: 800,
        lineHeight: 1.15,
        fontFamily: '"Noto Sans", sans-serif',
      },
      // Landing-page section eyebrow — "— SERVICES", "— QUICK ACCESS", etc.
      // QA-confirmed: 14px, weight 700, uppercase, tracking 0.12em (Figma node 359-19850).
      // Additive: does NOT change `overline` (10px) used elsewhere.
      sectionEyebrow: {
        fontSize: "0.875rem",
        fontWeight: 700,
        lineHeight: 1.2,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        fontFamily: '"Noto Sans", sans-serif',
      },

      // Landing-page section title — "Categories", "Popular Resources", "Forecast", etc.
      // QA-confirmed: 28px, weight 700 (Figma node 359-19850).
      // Additive: does NOT change `h2` (24px) used in cards, modals, detail pages.
      sectionTitle: {
        fontSize: "1.75rem",
        fontWeight: 700,
        lineHeight: 1.2,
        fontFamily: '"Noto Sans", sans-serif',
      },

      // Internal-screen page title (RewampPageHeader default) — 28px / 600 / lh 42px.
      // Distinct from `sectionTitle` (28/700): same size, lighter weight for
      // non-landing pages. Pass titleVariant="sectionTitle" to opt into 700 instead.
      pageHeader: {
        fontSize: "1.75rem",
        fontWeight: 600,
        lineHeight: 1.5,
        fontFamily: '"Noto Sans", sans-serif',
      },

      // Reusable in-page section header (SectionHeader component) — 20px / 600 / lh 30px.
      sectionHeader: {
        fontSize: "1.25rem",
        fontWeight: 600,
        lineHeight: 1.5,
        fontFamily: '"Noto Sans", sans-serif',
      },

      // Larger in-page section header (SectionHeader variant="sectionHeaderLarge") — 24px / 600.
      sectionHeaderLarge: {
        fontSize: "1.5rem",
        fontWeight: 600,
        lineHeight: 1.5,
        fontFamily: '"Noto Sans", sans-serif',
      },

      // Language picker tile glyph ("ए" / "A") — 60px / 600.
      languageGlyph: {
        fontSize: "3.75rem",
        fontWeight: 600,
        lineHeight: 1,
        fontFamily: '"Noto Sans", sans-serif',
      },

      drawerUserName: { // theme-new: user-approved PWA account drawer name (20 / 600 / 27)
        fontSize: "1.25rem",
        fontWeight: 600,
        lineHeight: 1.35,
        fontFamily: '"Noto Sans", sans-serif',
      },
      subsectionTitle: { // theme-new: user-approved PWA drawer block title (16 / 500 / 22)
        fontSize: "1rem",
        fontWeight: 500,
        lineHeight: 1.375,
        fontFamily: '"Noto Sans", sans-serif',
      },
      // Section / H2 — Major section headings
      h2: {
        fontSize: "1.5rem",
        fontWeight: 700,
        lineHeight: 1.2,
        fontFamily: '"Noto Sans", sans-serif',
      },
      // Card Title / H3
      h3: {
        fontSize: "1.125rem",
        fontWeight: 700,
        lineHeight: 1.3,
        fontFamily: '"Noto Sans", sans-serif',
      },
      // Subheading / H4
      h4: {
        fontSize: "0.9375rem",
        fontWeight: 600,
        lineHeight: 1.35,
        fontFamily: '"Noto Sans", sans-serif',
        "@media (max-width: 640px)": {
          fontSize: "0.875rem",
        },
      },
      // Body / Base
      h5: {
        fontSize: "0.875rem",
        fontWeight: 600,
        lineHeight: 1.6,
        fontFamily: '"Noto Sans", sans-serif',
      },
      // Label / UI
      h6: {
        fontSize: "0.75rem",
        fontWeight: 600,
        lineHeight: 1.3,
        fontFamily: '"Noto Sans", sans-serif',
      },
      h7: {
        fontSize: "0.625rem",
        fontWeight: 700,
        lineHeight: 1.2,
        letterSpacing: "0.14em",
        fontFamily: '"Noto Sans", sans-serif',
      },
      // Analytics metric value (Big numbers)
      analyticsValue: {
        fontSize: "2rem",
        fontWeight: 900,
        lineHeight: 1.25,
        fontFamily: '"Noto Sans", sans-serif',
        "@media (max-width: 600px)": {
          fontSize: "1.75rem",
        },
      },

      subtitle1: {
        fontSize: "1rem",
        fontWeight: 400,
        fontFamily: '"Noto Sans", sans-serif',
      },
      subtitle2: {
        fontSize: "0.875rem",
        fontWeight: 600,
        fontFamily: '"Noto Sans", sans-serif',
      },

      // Filter-row inline label — 14 / 500 / 20 (filterDropDown-recipe.md). Literal 500: the theme's
      // fontWeightMedium is remapped to 600 and Noto Sans 500 is loaded.
      filterLabel: {
        fontSize: "0.875rem",
        fontWeight: 500,
        lineHeight: "20px",
        fontFamily: '"Noto Sans", sans-serif',
      },

      // Card title — 16px / 600 / lh 24 (Figma node 625-122956). Additive: no
      // existing variant matches (h6=12, subtitle2=14/600, subtitle1=16/400).
      cardTitle: {
        fontSize: "1rem",
        fontWeight: 600,
        lineHeight: "1.5rem",
        fontFamily: '"Noto Sans", sans-serif',
      },

      // Form field label — replaces the .input-label CSS rule, whose
      // `font-family: Inter !important` forced a face this app never loads.
      fieldLabel: {
        fontSize: "0.875rem",
        fontWeight: 600,
        lineHeight: "1.125rem",
        fontFamily: '"Noto Sans", sans-serif',
      },
      // File-upload field caption — smaller, lighter treatment than every
      // other field label (replaces .file-upload-label).
      fieldLabelSmall: {
        fontSize: "0.75rem",
        fontWeight: 600,
        lineHeight: "1.125rem",
        fontFamily: '"Noto Sans", sans-serif',
      },

      // Scheme-details page title — 28px / 700 (Figma node 639-104132). Sits
      // between h1 (32/800) and h2 (24/700); no existing variant matches, so it
      // gets its own token rather than an sx size-override on a mismatched one.
      pageTitle: {
        fontSize: "1.75rem",
        fontWeight: 700,
        lineHeight: 1.35,
        fontFamily: '"Noto Sans", sans-serif',
        "@media (max-width: 640px)": {
          fontSize: "1.375rem",
        },
      },

      // Long-form reading copy — 20px / 400 / lh 1.6 (Figma node 639-104132:
      // "About this Scheme" body + scheme-info list). Larger than body/subtitle1
      // (16px) for the detail page's primary reading column.
      readingBody: {
        fontSize: "1.25rem",
        fontWeight: 400,
        lineHeight: 1.6,
        fontFamily: '"Noto Sans", sans-serif',
        "@media (max-width: 640px)": {
          fontSize: "1rem",
        },
      },

      // Confirm-dialog title/body; distinct from h3 (18/700). Colour is set by the caller.
      dialogTitle: {
        fontSize: "1.25rem",
        fontWeight: 600,
        lineHeight: 1,
        fontFamily: '"Noto Sans", sans-serif',
      },
      dialogBody: {
        fontSize: "1rem",
        fontWeight: 500,
        lineHeight: 1,
        fontFamily: '"Noto Sans", sans-serif',
      },

      // Body / Base — Primary paragraph text
      body1: {
        fontSize: "0.875rem",
        fontWeight: 400,
        lineHeight: 1.6,
        fontFamily: '"Noto Sans", sans-serif',
      },
      // Body Small — Secondary labels, descriptions
      body2: {
        fontSize: "0.8125rem",
        fontWeight: 400,
        lineHeight: 1.55,
        fontFamily: '"Noto Sans", sans-serif',
      },

      // Button text
      button: {
        fontSize: "0.875rem",
        fontWeight: 600,
        lineHeight: 1,
        textTransform: "none",
        fontFamily: '"Noto Sans", sans-serif',
      },

      // Caption — Label / UI
      caption: {
        fontSize: "0.75rem",
        fontWeight: 600,
        lineHeight: 1.3,
        fontFamily: '"Noto Sans", sans-serif',
      },

      // Overline — Eyebrow / Tag
      overline: {
        fontSize: "0.625rem",
        fontWeight: 700,
        textTransform: "uppercase",
        lineHeight: 1.2,
        letterSpacing: "0.14em",
        fontFamily: '"Noto Sans", sans-serif',
      },

      // Chip text
      chip: {
        fontSize: "0.75rem",
        fontWeight: 700,
        lineHeight: "114.286%",
        fontFamily: '"Noto Sans", sans-serif',
      },
    });
